import React from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import LaunchIcon from "@mui/icons-material/Launch";
import SpaIcon from "@mui/icons-material/Spa";
import BusinessIcon from "@mui/icons-material/Business";
import PsychologyIcon from "@mui/icons-material/Psychology";
import CodeIcon from "@mui/icons-material/Code";
import {Link as RouterLink} from "react-router";

export const Footer = () => {
    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    return (
        <Box
            component="footer"
            role="contentinfo"
            sx={{
                backgroundColor: "#173623",
                color: "#FFFFFF",
                pt: {xs: 6, md: 8},
                pb: {xs: 4, md: 5},
                mt: "auto",
                borderTop: "1px solid rgba(255, 255, 255, 0.08)",
                position: "relative",
                width: "100%",
                maxWidth: "100%",
                overflowX: "clip",
            }}
        >
            <Container maxWidth="lg" sx={{px: {xs: 2, sm: 3}}}>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {xs: "1fr", sm: "repeat(2, 1fr)", md: "1.2fr 1fr 1.2fr"},
                        gap: {xs: 4, md: 5},
                        width: "100%",
                        maxWidth: "100%",
                        minWidth: 0,
                    }}
                >
                    {/* Brand & Purpose */}
                    <Box sx={{minWidth: 0}}>
                        <Box sx={{display: "flex", alignItems: "center", gap: 1.5, mb: 2}}>
                            <SpaIcon sx={{color: "#A3E7B8", fontSize: 28}} />
                            <Typography
                                variant="h6"
                                component="div"
                                sx={{
                                    fontWeight: 700,
                                    letterSpacing: "-0.01em",
                                    color: "#FFFFFF",
                                }}
                            >
                                Kurs i bekymringsmestring
                            </Typography>
                        </Box>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "rgba(255, 255, 255, 0.78)",
                                lineHeight: 1.7,
                                mb: 2.5,
                                maxWidth: 360,
                            }}
                        >
                            Evidensbasert kursopplegg og verktøy for å møte bekymringer, grubling og hverdagsuro med vennlighet, tilstedeværelse og mestring.
                        </Typography>

                        <Box
                            sx={{
                                p: 1.5,
                                backgroundColor: "rgba(255, 255, 255, 0.05)",
                                borderRadius: 1.5,
                                border: "1px solid rgba(255, 255, 255, 0.08)",
                                maxWidth: 360,
                            }}
                        >
                            <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 0.5}}>
                                <PsychologyIcon sx={{color: "#A3E7B8", fontSize: 18}} />
                                <Typography
                                    variant="caption"
                                    component="div"
                                    sx={{fontWeight: 700, color: "#A3E7B8", textTransform: "uppercase", letterSpacing: "0.05em"}}
                                >
                                    Faglig opphav
                                </Typography>
                            </Box>
                            <Typography variant="caption" component="div" sx={{color: "rgba(255, 255, 255, 0.9)", lineHeight: 1.5}}>
                                Kurset er utviklet av <strong>Psykologspesialistene Serina Pedersen Vorland og Eirik Jørgensen Sollie</strong>.
                            </Typography>
                        </Box>
                    </Box>

                    {/* Navigation Links */}
                    <Box sx={{minWidth: 0}}>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                color: "#A3E7B8",
                                mb: 2,
                            }}
                        >
                            Sider og innhold
                        </Typography>
                        <Box
                            component="nav"
                            aria-label="Bunnmeny"
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.25,
                            }}
                        >
                            <Link
                                component={RouterLink}
                                to="/#om-kurset"
                                sx={{
                                    color: "rgba(255, 255, 255, 0.85)",
                                    textDecoration: "none",
                                    fontSize: "0.95rem",
                                    "&:hover": {color: "#FFFFFF", textDecoration: "underline"},
                                    "&:focus-visible": {outline: "2px solid #A3E7B8", outlineOffset: "2px"},
                                }}
                            >
                                Om kurset
                            </Link>
                            <Link
                                component={RouterLink}
                                to="/meditation"
                                sx={{
                                    color: "rgba(255, 255, 255, 0.85)",
                                    textDecoration: "none",
                                    fontSize: "0.95rem",
                                    "&:hover": {color: "#FFFFFF", textDecoration: "underline"},
                                    "&:focus-visible": {outline: "2px solid #A3E7B8", outlineOffset: "2px"},
                                }}
                            >
                                Meditasjonsøvelser & lydspor
                            </Link>
                            <Link
                                component={RouterLink}
                                to="/find-instructor"
                                sx={{
                                    color: "rgba(255, 255, 255, 0.85)",
                                    textDecoration: "none",
                                    fontSize: "0.95rem",
                                    "&:hover": {color: "#FFFFFF", textDecoration: "underline"},
                                    "&:focus-visible": {outline: "2px solid #A3E7B8", outlineOffset: "2px"},
                                }}
                            >
                                Finn kursholder i kart
                            </Link>
                            <Link
                                component={RouterLink}
                                to="/get-educated"
                                sx={{
                                    color: "rgba(255, 255, 255, 0.85)",
                                    textDecoration: "none",
                                    fontSize: "0.95rem",
                                    "&:hover": {color: "#FFFFFF", textDecoration: "underline"},
                                    "&:focus-visible": {outline: "2px solid #A3E7B8", outlineOffset: "2px"},
                                }}
                            >
                                Bli kursholder (for fagfolk)
                            </Link>
                            <Link
                                component={RouterLink}
                                to="/order-books"
                                sx={{
                                    color: "rgba(255, 255, 255, 0.85)",
                                    textDecoration: "none",
                                    fontSize: "0.95rem",
                                    "&:hover": {color: "#FFFFFF", textDecoration: "underline"},
                                    "&:focus-visible": {outline: "2px solid #A3E7B8", outlineOffset: "2px"},
                                }}
                            >
                                Bestill kursbøker
                            </Link>
                            <Link
                                component={RouterLink}
                                to="/news"
                                sx={{
                                    color: "rgba(255, 255, 255, 0.85)",
                                    textDecoration: "none",
                                    fontSize: "0.95rem",
                                    "&:hover": {color: "#FFFFFF", textDecoration: "underline"},
                                    "&:focus-visible": {outline: "2px solid #A3E7B8", outlineOffset: "2px"},
                                }}
                            >
                                Aktuelt og nyheter
                            </Link>
                        </Box>
                    </Box>

                    {/* Creator & Legal Company Information */}
                    <Box sx={{minWidth: 0}}>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                color: "#A3E7B8",
                                mb: 2,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <CodeIcon sx={{fontSize: 18}} />
                            Nettside & teknisk utvikling
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "rgba(255, 255, 255, 0.9)",
                                lineHeight: 1.6,
                                mb: 1,
                            }}
                        >
                            Nettsiden er utviklet og driftet av{" "}
                            <Link
                                href="https://oyvind.volden.family/"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    color: "#A3E7B8",
                                    fontWeight: 600,
                                    textDecoration: "underline",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    "&:hover": {color: "#FFFFFF"},
                                    "&:focus-visible": {outline: "2px solid #A3E7B8", outlineOffset: "2px"},
                                }}
                                aria-label="Øyvind Volden (åpnes i ny fane)"
                            >
                                Øyvind Volden
                                <LaunchIcon sx={{fontSize: 14}} />
                            </Link>
                        </Typography>
                        <Box
                            sx={{
                                color: "rgba(255, 255, 255, 0.75)",
                                fontSize: "0.85rem",
                                lineHeight: 1.7,
                                mt: 1.5,
                                p: 1.5,
                                backgroundColor: "rgba(255, 255, 255, 0.05)",
                                borderRadius: 1.5,
                                border: "1px solid rgba(255, 255, 255, 0.08)",
                            }}
                        >
                            <Typography variant="caption" component="div" sx={{fontWeight: 700, color: "#FFFFFF", mb: 0.5, display: "flex", alignItems: "center", gap: 0.75}}>
                                <BusinessIcon sx={{fontSize: 15, color: "#A3E7B8"}} />
                                VOLDEN (Enkeltpersonforetak)
                            </Typography>
                            <Typography variant="caption" component="div">
                                Org.nr: 925 411 337 MVA
                            </Typography>
                            <Typography variant="caption" component="div">
                                Adresse: Engjomvegen 45, 2611 Lillehammer, Norge
                            </Typography>
                            <Typography variant="caption" component="div" sx={{mt: 0.5, opacity: 0.85}}>
                                Konsulenttjenester innen programvareutvikling
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Divider sx={{borderColor: "rgba(255, 255, 255, 0.12)", my: {xs: 4, md: 5}}} />

                {/* Subfooter: Copyright and Scroll To Top */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: {xs: "column", sm: "row"},
                        justifyContent: "space-between",
                        alignItems: {xs: "flex-start", sm: "center"},
                        gap: 2,
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            color: "rgba(255, 255, 255, 0.65)",
                            fontSize: "0.8rem",
                            lineHeight: 1.6,
                        }}
                    >
                        © {new Date().getFullYear()} Kurs i bekymringsmestring. Kurset er utviklet av Serina Pedersen Vorland og Eirik Jørgensen Sollie. Teknisk utvikling og drift av VOLDEN.
                    </Typography>

                    <Tooltip title="Til toppen av siden" arrow>
                        <IconButton
                            onClick={scrollToTop}
                            aria-label="Rull til toppen av siden"
                            sx={{
                                color: "#FFFFFF",
                                backgroundColor: "rgba(255, 255, 255, 0.08)",
                                border: "1px solid rgba(255, 255, 255, 0.15)",
                                minWidth: 44,
                                minHeight: 44,
                                "&:hover": {
                                    backgroundColor: "rgba(255, 255, 255, 0.18)",
                                },
                                "&:focus-visible": {
                                    outline: "2px solid #A3E7B8",
                                    outlineOffset: "2px",
                                },
                            }}
                        >
                            <KeyboardArrowUpIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Container>
        </Box>
    );
};
